import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../lib/api.ts";
import { theme } from "../theme.ts";
import type { ModelComparisonResponse, ModelScore, ModelScoreSet } from "@march/shared";

export const MODEL_INFO: Record<string, { name: string; what: string }> = {
  current: {
    name: "Same-window formula",
    what: "Same 90 days in past years, averaged, × trailing-12-month growth. The original model.",
  },
  tuned: {
    name: "Tuned current",
    what: "Current, but one-off spikes are spread across the category instead of repeated on the same SKU, and a low-volume SKU's growth leans toward its category's.",
  },
  topdown: {
    name: "Top-down split",
    what: "Forecast each category's total, then split it by each SKU's share of the last 12 months.",
  },
  current_adj: {
    name: "Formula, stockout-adjusted",
    what: "The same-window formula on history corrected for stockouts: a past window the SKU was partly out for is scaled up to what it sold while in stock (at most double); a mostly-out window counts as missing.",
  },
  topdown_adj: {
    name: "Top-down, stockout-adjusted",
    what: "The top-down split on stockout-corrected history, so a SKU that was out of stock for part of the last 12 months is not given a smaller share for it.",
  },
  current_lost: {
    name: "Formula + cancelled orders",
    what: "The same-window formula with cancelled and closed order lines added back into history — sales customers asked for and did not get. Intercompany orders excluded.",
  },
  topdown_lost: {
    name: "Top-down + cancelled orders",
    what: "The top-down split with cancelled order lines added back, so a SKU that lost orders to shortages keeps its share.",
  },
  pace: {
    name: "In use + category pace",
    what: "The model in use for each tab, nudged halfway toward how the category has sold over the last 28 days against what it forecast (limited to ±50%; one-off spikes capped).",
  },
  pace_sku: {
    name: "In use + category & SKU pace",
    what: "As above, plus a gentler per-SKU pace for SKUs with enough recent volume — a SKU selling hotter than its category rises, a quiet one falls.",
  },
  ets: {
    name: "Seasonal smoothing",
    what: "Damped Holt-Winters on each SKU's monthly history (level, trend, season). Uses the current model where history is under 24 months.",
  },
};
const ORDER = ["current", "current_adj", "current_lost", "tuned", "topdown", "topdown_adj", "topdown_lost", "pace", "pace_sku", "ets"];

const pct = (x: number | null, sign = false) =>
  x == null ? "—" : `${sign && x > 0 ? "+" : ""}${Math.round(x * 100)}%`;

/** Mirrors models.ts PRODUCTION_MODEL: the model each Forecast tab uses. */
const IN_USE: Record<string, string> = {
  Frame: "topdown",
  Cover: "topdown",
  Base: "pace",
  Fabric: "topdown_lost",
  Trim: "topdown",
  ProtectiveCover: "topdown",
};

function ScoreTable({ set, inUse }: { set: ModelScoreSet; inUse: string }) {
  const models = [...set.models].sort((a, b) => ORDER.indexOf(a.model) - ORDER.indexOf(b.model));
  const best = models.reduce<ModelScore | null>(
    (b, m) => (m.wape != null && (b == null || (b.wape ?? Infinity) > m.wape) ? m : b),
    null,
  );
  const cur = models.find((m) => m.model === "current");
  const th: React.CSSProperties = {
    textAlign: "right",
    padding: "6px 8px",
    fontSize: theme.font.size.xs,
    color: theme.color.textMuted,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.3,
    whiteSpace: "nowrap",
  };
  const td: React.CSSProperties = {
    textAlign: "right",
    padding: "8px",
    fontVariantNumeric: "tabular-nums",
    borderTop: `1px solid ${theme.color.border}`,
  };
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: theme.font.size.sm }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: "left" }}>Model</th>
            <th style={th} title="Total units missed ÷ total units sold, SKU by SKU. Big SKUs count for more. Lower is better.">
              Error (weighted)
            </th>
            <th style={th}>vs formula</th>
            <th style={th} title="Total forecast vs total sold">Total bias</th>
            <th style={th}>SKUs within ±25%</th>
            <th style={th}>Within ±50%</th>
            <th style={th} title="Windows where this model had the lowest weighted error">Windows won</th>
          </tr>
        </thead>
        <tbody>
          {models.map((m) => {
            const info = MODEL_INFO[m.model] ?? { name: m.model, what: "" };
            const isBest = best?.model === m.model;
            const delta =
              m.model !== "current" && m.wape != null && cur?.wape
                ? m.wape / cur.wape - 1
                : null;
            return (
              <tr key={m.model} style={{ background: isBest ? theme.color.successFaint : undefined }}>
                <td style={{ ...td, textAlign: "left", maxWidth: 380 }}>
                  <div style={{ fontWeight: 600 }}>
                    {info.name}
                    {m.model === inUse && (
                      <span style={{ marginLeft: 6, fontSize: 10, color: theme.color.textMuted }}>IN USE</span>
                    )}
                    {isBest && (
                      <span style={{ marginLeft: 6, fontSize: 10, color: theme.color.successText }}>BEST</span>
                    )}
                  </div>
                  <div style={{ fontSize: theme.font.size.xs, color: theme.color.textMuted }}>{info.what}</div>
                </td>
                <td style={{ ...td, fontWeight: 700 }}>{pct(m.wape)}</td>
                <td
                  style={{
                    ...td,
                    color: delta == null ? theme.color.textFaint : delta < 0 ? theme.color.successText : theme.color.dangerText,
                  }}
                >
                  {delta == null ? "—" : `${delta < 0 ? "" : "+"}${Math.round(delta * 100)}%`}
                </td>
                <td style={td}>{pct(m.bias, true)}</td>
                <td style={td}>{pct(m.within25)}</td>
                <td style={td}>{pct(m.within50)}</td>
                <td style={td}>
                  {m.wins} of {set.windows}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Challenger models scored against the current one on the same windows. */
export function ModelComparison({ type }: { type: string }) {
  const [range, setRange] = useState<"recent" | "all">("recent");
  const [scope, setScope] = useState<"all" | "instock" | "true">("all");
  const q = useQuery<ModelComparisonResponse>({
    queryKey: ["accuracy-models", type, scope],
    queryFn: () => apiGet<ModelComparisonResponse>(`/api/accuracy/models?type=${type}&scope=${scope}`),
  });
  const d = q.data;
  if (q.isLoading) return <div style={{ color: theme.color.textMuted }}>Loading…</div>;
  if (q.error) return <div style={{ color: theme.color.danger }}>{(q.error as Error).message}</div>;
  if (!d) return null;
  const bt = d.backtest[range];
  const live = d.live.all;
  return (
    <div style={{ display: "grid", gap: theme.space.md }}>
      <div style={{ display: "flex", gap: theme.space.sm, alignItems: "center", flexWrap: "wrap" }}>
        <span className="seg sm" role="group" aria-label="Windows">
          <button type="button" aria-pressed={range === "recent"} onClick={() => setRange("recent")}>Last {d.backtest.recent.windows} windows</button>
          <button type="button" aria-pressed={range === "all"} onClick={() => setRange("all")}>All {d.backtest.all.windows}</button>
        </span>
        <span className="seg sm" role="group" aria-label="Score against">
          <button type="button" aria-pressed={scope === "all"} onClick={() => setScope("all")}>All SKU-windows</button>
          <button type="button" aria-pressed={scope === "instock"} onClick={() => setScope("instock")}>Only while in stock</button>
          <button type="button" aria-pressed={scope === "true"} onClick={() => setScope("true")}>Shipped + cancelled</button>
        </span>
      </div>
      <div style={{ fontSize: theme.font.size.xs, color: theme.color.textFaint, lineHeight: 1.5 }}>
        Backtest: each model re-run as of past Mondays with only the data it would have had.{" "}
        {scope === "instock"
          ? "Scoring only SKU-windows with at most 9 days out of stock, where what shipped is a fair measure of what customers wanted."
          : scope === "true"
          ? "Scoring against what shipped plus what was ordered and cancelled — closer to what customers actually asked for."
          : "Scoring every SKU-window. Where a SKU ran out, what shipped understates demand — so a model that forecasts true demand can look worse here than it is. Compare with “Only while in stock”."}
      </div>
      {bt.windows > 0 ? (
        <ScoreTable set={bt} inUse={IN_USE[type] ?? "current"} />
      ) : (
        <div style={{ color: theme.color.textMuted }}>No backtest windows scored yet.</div>
      )}
      <div style={{ fontWeight: 600, marginTop: theme.space.sm }}>Live results</div>
      {live.windows > 0 ? (
        <ScoreTable set={live} inUse={IN_USE[type] ?? "current"} />
      ) : (
        <div style={{ fontSize: theme.font.size.sm, color: theme.color.textMuted }}>
          Every model is now frozen weekly alongside the real forecast
          {d.live_pending ? ` (${d.live_pending} waiting so far)` : ""}. Their first 90-day windows
          close in about three months, and live scores appear here from then on. Live results are
          the real test: the backtest can be tuned to the past, live windows cannot.
        </div>
      )}
    </div>
  );
}
